import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, Typography, App, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../context/AuthContext';
import { thoughtsApi, ThoughtInput } from '../api/thoughts';
import { modulesApi } from '../api/modules';
import { lessonsApi } from '../api/lessons';
import { Thought, CourseModule, Lesson } from '../api/types';

const { Title, Text } = Typography;

interface ThoughtFormValues {
  moduleId: number;
  lessonId?: number;
  body: string;
  subBody?: string | null;
  rotationNumber: number;
}

const ThoughtsPage: React.FC = () => {
  const { apiClient } = useAuth();
  const { message, modal } = App.useApp();

  const [thoughts, setThoughts] = useState<Thought[]>([]);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Thought | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm<ThoughtFormValues>();

  const tApi = useCallback(() => thoughtsApi(apiClient!), [apiClient]);
  const mApi = useCallback(() => modulesApi(apiClient!), [apiClient]);
  const lApi = useCallback(() => lessonsApi(apiClient!), [apiClient]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [thoughtsRes, modulesRes] = await Promise.all([tApi().getList(), mApi().getAll()]);
      setThoughts(thoughtsRes.data.items);
      setModules(modulesRes.data.items);
    } catch {
      message.error('Failed to load thoughts');
    } finally {
      setLoading(false);
    }
  }, [tApi, mApi, message]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadLessons = useCallback(async (moduleId: number) => {
    try {
      const res = await lApi().getList(moduleId);
      setLessons(res.data.items);
    } catch {
      setLessons([]);
      message.error('Failed to load lessons');
    }
  }, [lApi, message]);

  const openCreate = () => {
    setEditing(null);
    setLessons([]);
    form.resetFields();
    form.setFieldsValue({ rotationNumber: 1 });
    setModalOpen(true);
  };

  const openEdit = async (t: Thought) => {
    setEditing(t);
    await loadLessons(t.moduleId);
    form.setFieldsValue({
      moduleId: t.moduleId,
      lessonId: t.lessonId,
      body: t.body,
      subBody: t.subBody ?? '',
      rotationNumber: t.rotationNumber,
    });
    setModalOpen(true);
  };

  const onModuleChange = (moduleId: number) => {
    form.setFieldsValue({ lessonId: undefined });
    loadLessons(moduleId);
  };

  const handleSave = async (values: ThoughtFormValues) => {
    setSaving(true);
    try {
      const payload: ThoughtInput = {
        body: values.body,
        subBody: values.subBody ? values.subBody : null,
        lessonId: values.lessonId!,
        rotationNumber: values.rotationNumber,
      };
      if (editing) {
        await tApi().update({ ...payload, id: editing.id });
        message.success('Thought updated');
      } else {
        await tApi().create(payload);
        message.success('Thought created');
      }
      setModalOpen(false);
      await fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to save thought');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (t: Thought) => {
    modal.confirm({
      title: 'Delete thought?',
      content: `This will permanently delete thought #${t.id}. This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await tApi().delete(t.id);
          message.success('Thought deleted');
          await fetchData();
        } catch {
          message.error('Failed to delete thought');
        }
      },
    });
  };

  const handleBatchDelete = () => {
    modal.confirm({
      title: `Delete ${selectedRowKeys.length} thought(s)?`,
      content: 'Selected thoughts will be permanently deleted. This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await tApi().batchDelete(selectedRowKeys.map(Number));
          message.success(`Deleted ${selectedRowKeys.length} thought(s)`);
          setSelectedRowKeys([]);
          await fetchData();
        } catch {
          message.error('Failed to delete thoughts');
        }
      },
    });
  };

  const moduleOptions = modules.map((m) => ({ value: m.id, label: `[${m.courseId}] ${m.title}` }));
  const lessonOptions = lessons.map((l) => ({ value: l.id, label: l.title }));

  const columns: ColumnsType<Thought> = [
    { title: 'Id', dataIndex: 'id', width: 60 },
    {
      title: 'Body',
      dataIndex: 'body',
      render: (b: string) => <Text style={{ maxWidth: 420 }} ellipsis={{ tooltip: b }}>{b}</Text>,
    },
    { title: 'Course', dataIndex: 'courseTitle', width: 180 },
    { title: 'Lesson', dataIndex: 'lessonTitle', width: 180 },
    { title: 'Rotation', dataIndex: 'rotationNumber', width: 90 },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, t) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(t)} />
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(t)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Thoughts of the Day</Title>
        <Space>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={handleBatchDelete}
          >
            Delete selected{selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ''}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Thought</Button>
        </Space>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={thoughts}
        columns={columns}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        pagination={false}
      />

      <Modal
        open={modalOpen}
        title={editing ? `Edit Thought #${editing.id}` : 'New Thought'}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item label="Module" name="moduleId" rules={[{ required: true, message: 'Select a module' }]}>
            <Select
              placeholder="Select module"
              options={moduleOptions}
              optionFilterProp="label"
              showSearch
              onChange={onModuleChange}
            />
          </Form.Item>
          <Form.Item label="Lesson" name="lessonId" rules={[{ required: true, message: 'Select a lesson' }]}>
            <Select
              placeholder="Select lesson"
              options={lessonOptions}
              optionFilterProp="label"
              showSearch
              disabled={lessonOptions.length === 0}
            />
          </Form.Item>
          <Form.Item label="Body" name="body" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Sub body (optional, admin-only)" name="subBody">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Rotation number" name="rotationNumber" rules={[{ required: true, type: 'number', min: 1 }]}>
            <InputNumber min={1} style={{ width: 200 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ThoughtsPage;
